import type React from 'react';

export interface HelpEntry {
  content: string;
  variables?: Record<string, unknown>;
  injectableComponents?: Record<string, React.ReactNode>;
}

/** Live registry — each mounted HelpButton registers its content (with variables) */
export const helpRegistry = new Map<string, React.MutableRefObject<HelpEntry>>();

/**
 * Static content fallback — maps display names (as they appear in backtick
 * deep links) to raw markdown.  Populated at module-load time so deep links
 * work even when the target HelpButton isn't mounted.
 */
export const helpContentFallback = new Map<string, string>();

/** Register static help content for deep-link targets. Call at module scope. */
export function registerHelpContent(entries: Record<string, string>) {
  for (const [name, content] of Object.entries(entries)) {
    helpContentFallback.set(name, content);
  }
}

/**
 * Look up help content by field name.
 * Prefers the live helpRegistry (has current variables/injectables),
 * falls back to the static helpContentFallback.
 */
export function resolveHelpEntry(fieldName: string): HelpEntry | null {
  const live = helpRegistry.get(fieldName);
  if (live) return live.current;
  const staticContent = helpContentFallback.get(fieldName);
  if (staticContent) return { content: staticContent };
  return null;
}
